import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { toast } from 'react-toastify';
import { Group, Hint, ItemButton, Label, Wrapper } from './styles';

export type SegmentedOptionValue = string | number;

export interface SegmentedOption {
  label: string;
  value: SegmentedOptionValue;
  disabled?: boolean;
}

type SelectionMode = 'single' | 'multi';

export interface SegmentedPickerProps {
  label?: string;
  options: SegmentedOption[];
  mode: SelectionMode;
  maxSelected?: number;

  selected?: SegmentedOptionValue[];
  defaultSelected?: SegmentedOptionValue[];

  onChange?: (selectedValues: SegmentedOptionValue[]) => void;

  disabled?: boolean;

  messages?: {
    multiLimitReached?: (limit: number) => string;
    needToDeselectBefore?: string;
  };

  ariaLabel?: string;
  id?: string;
  className?: string;
  equalizeItemWidths?: boolean;
}

export const SegmentedPicker: React.FC<SegmentedPickerProps> = ({
  label,
  options,
  mode,
  maxSelected,
  selected,
  defaultSelected,
  onChange,
  disabled = false,
  messages,
  ariaLabel,
  id,
  className,
  equalizeItemWidths = false,
}) => {
  if (!Array.isArray(options) || options.length < 2) {
    throw new Error(
      'SegmentedPicker: "options" deve conter pelo menos 2 itens.',
    );
  }

  if (mode === 'multi') {
    if (typeof maxSelected !== 'number' || maxSelected < 1) {
      throw new Error(
        'SegmentedPicker: em modo "multi", defina "maxSelected" >= 1.',
      );
    }
    if (maxSelected > options.length) {
      // eslint-disable-next-line no-console
      console.warn(
        `SegmentedPicker: "maxSelected" (${maxSelected}) é maior que a quantidade de options (${options.length}).`,
      );
    }
  }

  const controlledSelected = useMemo(
    () => (Array.isArray(selected) ? selected : undefined),
    [selected],
  );
  const isControlled = controlledSelected !== undefined;

  const [internal, setInternal] = useState<SegmentedOptionValue[]>(() =>
    (defaultSelected ?? []).filter((v) => v !== undefined),
  );

  const btnRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const [maxItemWidth, setMaxItemWidth] = useState(0);

  const selectedValues = controlledSelected ?? internal;

  useEffect(() => {
    if (!isControlled && defaultSelected) setInternal(defaultSelected);
  }, [isControlled, defaultSelected]);

  const emitChange = (next: SegmentedOptionValue[]) => {
    if (!isControlled) setInternal(next);
    onChange?.(next);
  };

  const onItemClick = (
    value: SegmentedOptionValue,
    isItemDisabled?: boolean,
  ) => {
    if (disabled || isItemDisabled) return;

    if (mode === 'single') {
      if (selectedValues.includes(value)) return;
      emitChange([value]);
      return;
    }

    const exists = selectedValues.includes(value);
    if (exists) {
      emitChange(selectedValues.filter((v) => v !== value));
      return;
    }

    const limit = maxSelected as number;
    if (selectedValues.length >= limit) {
      const msg =
        messages?.needToDeselectBefore ??
        'Limite atingido. Desselecione um item para escolher outro.';
      toast.warn(msg);
      return;
    }

    emitChange([...selectedValues, value]);
  };

  const limitReached =
    mode === 'multi'
      ? (selectedValues.length ?? 0) >= (maxSelected as number)
      : false;

  const groupAriaLabel =
    ariaLabel ??
    (label
      ? `${label} seletor ${mode === 'single' ? 'único' : 'múltiplo'}`
      : 'Segmented picker');

  const setBtnRef = useCallback((el: HTMLButtonElement | null, idx: number) => {
    btnRefs.current[idx] = el;
  }, []);

  const recomputeMaxWidth = useCallback(() => {
    const widths = btnRefs.current.map((el) =>
      el ? el.getBoundingClientRect().width : 0,
    );
    const next = widths.length ? Math.max(...widths) : 0;
    setMaxItemWidth((prev) =>
      Math.ceil(prev) !== Math.ceil(next) ? next : prev,
    );
  }, []);

  const optionsCount = options.length;
  const labelsSignature = React.useMemo(
    () => options.map((o) => o.label).join('|'),
    [options],
  );

  useEffect(() => {
    if (!equalizeItemWidths) return;

    recomputeMaxWidth();

    let ro: ResizeObserver | undefined;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(() => recomputeMaxWidth());
      btnRefs.current.forEach((el) => {
        if (ro && el) ro.observe(el);
      });
    } else {
      const onResize = () => recomputeMaxWidth();
      window.addEventListener('resize', onResize);
      return () => window.removeEventListener('resize', onResize);
    }

    return () => {
      ro?.disconnect();
    };
  }, [equalizeItemWidths, optionsCount, labelsSignature, recomputeMaxWidth]);

  const groupStyle =
    equalizeItemWidths && maxItemWidth
      ? ({
          ['--seg-item-width' as any]: `${Math.ceil(maxItemWidth)}px`,
        } as React.CSSProperties)
      : undefined;

  return (
    <Wrapper className={className} data-disabled={disabled} id={id}>
      {label ? <Label>{label}</Label> : null}

      <Group
        role="group"
        aria-label={groupAriaLabel}
        aria-disabled={disabled}
        data-limit-reached={limitReached && mode === 'multi'}
        style={groupStyle}
      >
        {options.map((opt, index) => {
          const isSelected = selectedValues.includes(opt.value);
          const isItemDisabled = disabled || opt.disabled;
          return (
            <ItemButton
              key={String(opt.value)}
              type="button"
              aria-pressed={isSelected}
              aria-disabled={isItemDisabled}
              data-selected={isSelected}
              disabled={isItemDisabled}
              onClick={() => onItemClick(opt.value, opt.disabled)}
              title={opt.label}
              ref={(el) => setBtnRef(el, index)}
            >
              {opt.label}
            </ItemButton>
          );
        })}
      </Group>

      {mode === 'multi' && typeof maxSelected === 'number' ? (
        <Hint role="note" aria-live="polite">
          {selectedValues.length}/{maxSelected}{' '}
          {messages?.multiLimitReached
            ? messages.multiLimitReached(maxSelected)
            : 'selecionados'}
        </Hint>
      ) : null}
    </Wrapper>
  );
};
